import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import WebApp from '@twa-dev/sdk'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Dropdown, DropdownItem } from 'flowbite-react';
import { useBackButton } from '../hooks/useBackButton';

const SourceType = {
    'massmedia': 'СМИ',
    'telegram': 'Telegram',
    'social_network': 'Социальная сеть'
};

const SourceCategory = {
    'industry': 'Отраслевой',
    'news': 'Новостной',
    'aggregator': 'Агрегатор',
    'regional': 'Региональный',
    'local': 'Локальный',
    'compromat': 'Компроматный',
    'personal': 'Личный профиль',
    'private': 'Закрытый профиль',
};

const SourceRating = {
    'junk': 'Мусорный',
    'low': 'Низкий',
    'low_plus': 'Низкий (с охватами)',
    'neutral': 'Нейтральный',
    'neutral_plus': 'Нейтральный (с охватами)',
    'high': 'Авторитетный',
    'high_plus': 'Авторитетный (с охватами)'
};

function CategoryDropdown({ onSelect }) {
    return (
        <Dropdown label="" dismissOnClick={true} className="rounded-xl shadow-md focus:outline-none border-none outline-none"
            renderTrigger={() => <FontAwesomeIcon icon={faPencil} size="xs" className="text-tg-text" />}>
        {Object.entries(SourceCategory).map(([value, label]) => (
        <DropdownItem onClick={() => onSelect(value)} key={value}><b>{label}</b></DropdownItem>
        ))}
      </Dropdown>
    );
}

function RatingDropdown({ onSelect }) {
    return (
        <Dropdown label="" dismissOnClick={true} className="rounded-xl shadow-md focus:outline-none border-none outline-none"
         renderTrigger={() => <FontAwesomeIcon icon={faPencil} size="xs" className="text-tg-text" />
    }>
        {Object.entries(SourceRating).map(([value, label]) => (
          <DropdownItem onClick={() => onSelect(value)} key={value}><b>{label}</b></DropdownItem>
        ))}
      </Dropdown>
    );
}

const SourcePage = ({ messageId }) => {
    const [source, setSource] = useState(null);
    const [isFormOpen, setFormOpen] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const textareaRef = useRef();

    const toggleForm = () => {
        setFormOpen(!isFormOpen);
    };

    const handleBackClick = useCallback(() => {
        setFormOpen(false);
    }, []);

    useBackButton({
        onClick: handleBackClick,
        visible: isFormOpen,
    });

    useEffect(() => {
        if (isFormOpen) {
            WebApp.enableClosingConfirmation();
        } else {
            WebApp.disableClosingConfirmation();
        }
        return () => WebApp.disableClosingConfirmation();
    }, [isFormOpen]);

    const setSourceCategory = async (value) => {
        WebApp.HapticFeedback.impactOccurred('light');
        const response = await api.post('/api/source/set', {
            message_id: messageId,
            category: value
        });

        if (response.status === 200) {
            WebApp.HapticFeedback.notificationOccurred('success');
            fetchSource();
        }
    };

    const setSourceRating = async (value) => {
        WebApp.HapticFeedback.impactOccurred('light');
        const response = await api.post('/api/source/set', {
            message_id: messageId,
            rating: value
        });

        if (response.status === 200) {
            WebApp.HapticFeedback.notificationOccurred('success');
            fetchSource();
        }
    };

    const setSourceDescription = async (value) => {
        setSubmitting(true);
        try {
            const response = await api.post('/api/source/set', {
              message_id: messageId,
              description: value
            });
            if (response.status === 200) {
              WebApp.HapticFeedback.notificationOccurred('success');
              fetchSource();
              setFormOpen(false);
            } else {
                WebApp.showAlert('Произошла ошибка при сохранении описания.');
                WebApp.HapticFeedback.notificationOccurred('error');
            }
        } catch (error) {
            WebApp.showAlert('Произошла ошибка при сохранении описания.');
            WebApp.HapticFeedback.notificationOccurred('error');
        }
        setSubmitting(false);
      };

    const fetchSource = async () => {
        setFetchError(false);
        try {
            const response = await api.get(`/api/source/${messageId}`);
            setSource(response.data);
        } catch (error) {
            console.error('Failed to fetch source:', error);
            setFetchError(true);
        }
    };

    useEffect(() => {
        fetchSource();
    }, [messageId]);

    if (fetchError) {
        return (
            <div className="flex flex-col items-center justify-center h-screen gap-4">
                <p className="text-tg-hint">Не удалось загрузить информацию об источнике</p>
                <button onClick={fetchSource} className="py-2 px-4 rounded-lg bg-tg-button text-tg-button-text border-none cursor-pointer">
                    Повторить
                </button>
            </div>
        );
    }

    if (!source) {
        return (
            <div className="flex justify-center items-center h-screen">
                <FontAwesomeIcon icon={faSpinner} spin className="text-tg-hint text-2xl" />
            </div>
        );
    }

    return (
        <div className="p-4 bg-tg-bg text-tg-text">
            <div className="max-w-md mx-auto rounded-xl shadow-md md:max-w-2xl m-4 bg-tg-bg text-tg-text">
                <div className="md:flex">
                    <div className="p-5">
                        <div className="uppercase tracking-wide text-sm font-semibold text-tg-accent-text">
                            {SourceType[source.source_type]}
                        </div>
                        <p className="block mt-1 text-lg leading-tight font-medium text-tg-text">
                            {source.name}
                        </p>
                        <div className="mt-2">
                            <span>Название: </span><span className="pr-2"><b>{source.display_name}</b></span>
                        </div>
                        <div className="mt-4">
                            <span>Категория: </span><span className="pr-2"><b>{SourceCategory[source.category] || "не задано"}</b></span>
                            <CategoryDropdown onSelect={setSourceCategory} />
                        </div>
                        <div className="mt-2">
                            <span>Рейтинг: </span><span className="pr-2"><b>{SourceRating[source.rating] || "не задано"}</b></span>
                            <RatingDropdown onSelect={setSourceRating} />
                        </div>
                        {source.subscribers && (
                            <div className="mt-2">
                                <span>Количество подписчиков: </span><span><b>{source.subscribers}</b></span>
                            </div>
                        )}
                        {source.is_able_to_get_description && (
                            <div className="mt-2">
                            <span>Описание:</span><br/>
                            {!isFormOpen ? (
                              <span>
                                <b className="pr-2">{source.description || "не задано"}</b>
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  size="xs"
                                  className="text-tg-text"
                                  onClick={toggleForm}
                                />
                              </span>
                            ) : (
                            <form onSubmit={(e) => { e.preventDefault(); setSourceDescription(textareaRef.current.value); }}>
                                <textarea
                                  className="border rounded-lg p-1 w-full max-w-[600px] bg-tg-bg text-tg-text border-tg-secondary-bg outline-none mb-1"
                                  defaultValue={source.description || ""}
                                  ref={textareaRef}
                                />
                                <button
                                    type="submit"
                                    className="font-bold py-2 px-4 rounded-lg border-none outline-none cursor-pointer w-full max-w-[600px] bg-tg-button text-tg-button-text"
                                    disabled={isSubmitting}
                                >
                                    Сохранить
                                </button>
                              </form>
                            )}
                          </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SourcePage;
